import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { getHabitIdsWithLogs, updateCompetition } from '@/lib/firebase/queries/competitions';
import type { Competition, CompetitionHabit } from '@/types/competitions';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { CompetitionHabitsEditor } from './CompetitionHabitsEditor';

// Create schemas as functions to access translations
const createCompetitionSchema = (t: (key: string) => string) => z.object({
  name: z.string().min(1, t('dashboard.manager.competitions.validation.nameRequired') || 'Competition name is required'),
  description: z.string().optional(),
  startDate: z.date({
    required_error: t('dashboard.manager.competitions.validation.startDateRequired') || 'Start date is required',
  }),
  endDate: z.date({
    required_error: t('dashboard.manager.competitions.validation.endDateRequired') || 'End date is required',
  }),
}).refine((data) => data.endDate > data.startDate, {
  message: t('dashboard.manager.competitions.validation.endDateAfterStart') || 'End date must be after start date',
  path: ['endDate'],
});

interface EditCompetitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competition: Competition;
  onSuccess?: () => void;
}

export function EditCompetitionDialog({
  open,
  onOpenChange,
  competition,
  onSuccess
}: EditCompetitionDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  // Habits that already have logs keep their category (namat#25). Null while
  // loading, when every existing habit is treated as locked.
  const [habitIdsWithLogs, setHabitIdsWithLogs] = useState<Set<string> | null>(null);
  const [habits, setHabits] = useState<CompetitionHabit[]>(competition.habits);
  const [isPublished, setIsPublished] = useState(competition.isPublished !== false);
  const { t, dir } = useLanguage();

  const competitionSchema = createCompetitionSchema(t);

  const form = useForm<z.infer<typeof competitionSchema>>({
    resolver: zodResolver(competitionSchema),
    defaultValues: {
      name: competition.name,
      description: competition.description || '',
      startDate: competition.startDate,
      endDate: competition.endDate,
    },
  });

  // Reset form state whenever a different competition is opened for editing
  useEffect(() => {
    if (open) {
      form.reset({
        name: competition.name,
        description: competition.description || '',
        startDate: competition.startDate,
        endDate: competition.endDate,
      });
      setHabits(competition.habits);
      setIsPublished(competition.isPublished !== false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, competition]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setHabitIdsWithLogs(null);
    getHabitIdsWithLogs(competition.id, competition.habits.map(h => h.id))
      .then(ids => { if (!cancelled) setHabitIdsWithLogs(ids); })
      .catch(error => {
        console.error('Error checking competition habit logs:', error);
        // Keep existing habits locked rather than risk a category change.
        if (!cancelled) setHabitIdsWithLogs(new Set(competition.habits.map(h => h.id)));
      });
    return () => { cancelled = true; };
  }, [open, competition]);

  const savedHabitIds = new Set(competition.habits.map(h => h.id));
  const isCategoryLocked = (habitId: string) =>
    habitIdsWithLogs === null ? savedHabitIds.has(habitId) : habitIdsWithLogs.has(habitId);

  const onSubmit = async (values: z.infer<typeof competitionSchema>) => {
    if (habits.length === 0) {
      toast.error(t('dashboard.manager.competitions.messages.addHabitsRequired') || 'Please add at least one habit to the competition');
      return;
    }

    try {
      setIsLoading(true);

      await updateCompetition(competition.id, {
        name: values.name,
        description: values.description,
        startDate: values.startDate,
        endDate: values.endDate,
        habits,
        isPublished,
      });

      toast.success(t('dashboard.manager.competitions.messages.updatedSuccessfully') || 'Competition updated');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating competition:', error);

      // Check for specific error types
      if (error instanceof Error) {
        if (error.message.includes('Organization already has an active competition')) {
          toast.error(t('dashboard.manager.competitions.messages.activeCompetitionExists') || 'Organization already has an active competition. Only one active competition is allowed per organization.');
        } else if (error.message.includes('competition that overlaps with these dates')) {
          toast.error(t('dashboard.manager.competitions.messages.overlappingCompetitionExists') || 'Organization already has a competition that overlaps with these dates. Competitions cannot have overlapping time periods.');
        } else {
          toast.error(t('dashboard.manager.competitions.messages.updateFailed') || 'Failed to update competition');
        }
      } else {
        toast.error(t('dashboard.manager.competitions.messages.updateFailed') || 'Failed to update competition');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader>
          <DialogTitle className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {t('dashboard.manager.competitions.editCompetition') || 'Edit Competition'}
          </DialogTitle>
          <DialogDescription className={dir === 'rtl' ? 'text-right' : 'text-left'}>
            {t('dashboard.manager.competitions.editCompetitionDesc') || 'Update this competition for your organization members'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dashboard.manager.competitions.name') || 'Competition Name'}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('dashboard.manager.competitions.namePlaceholder') || 'Enter competition name'} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dashboard.manager.competitions.description') || 'Description'}</FormLabel>
                    <FormControl>
              <Textarea
                        placeholder={t('dashboard.manager.competitions.descriptionPlaceholder') || 'Enter competition description (optional)'}
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t('dashboard.manager.competitions.startDate') || 'Start Date'}</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                        <FormControl>
                  <Button
                    variant="outline"
                    className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                    )}
                  >
                            {field.value ? (
                              format(field.value, "PPP")
                    ) : (
                              <span>{t('dashboard.manager.competitions.pickStartDate') || 'Pick a start date'}</span>
                    )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                        </FormControl>
                </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const compareDate = new Date(date);
                            compareDate.setHours(0, 0, 0, 0);
                            return compareDate < today;
                          }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t('dashboard.manager.competitions.endDate') || 'End Date'}</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                        <FormControl>
                  <Button
                    variant="outline"
                    className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                    )}
                  >
                            {field.value ? (
                              format(field.value, "PPP")
                    ) : (
                              <span>{t('dashboard.manager.competitions.pickEndDate') || 'Pick an end date'}</span>
                    )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                        </FormControl>
                </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const compareDate = new Date(date);
                            compareDate.setHours(0, 0, 0, 0);
                            return compareDate < today;
                          }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
          </div>

          <CompetitionHabitsEditor habits={habits} onChange={setHabits} isCategoryLocked={isCategoryLocked} />

          {/* Publish visibility */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="edit-competition-published" className="text-sm font-medium">
                {t('dashboard.manager.competitions.isPublishedLabel') || 'Visible to beneficiaries'}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('dashboard.manager.competitions.isPublishedDescription') || "Beneficiaries can see and join this competition once it's published."}
              </p>
            </div>
            <Switch
              id="edit-competition-published"
              checked={isPublished}
              onCheckedChange={setIsPublished}
            />
          </div>

            <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
                {t('dashboard.manager.competitions.cancel') || 'Cancel'}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (t('dashboard.manager.competitions.saving') || 'Saving...') : (t('dashboard.manager.competitions.saveChanges') || 'Save Changes')}
            </Button>
            </DialogFooter>
        </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
